// Dataset model — pure helpers over a dataset's `meta`, DOM- and state-free.
//
// These answer "how is this dataset shaped?" — which dim splits into lines,
// which dims are single-select filters, what the factory-default selection is,
// and which level/selection combinations are possible. They mirror the R side
// (ds_split_of in api/query.R) so the client and the static renderer agree.

// Loose shapes: `meta` grows optional fields across sources; keep it permissive
// here and let the zod schema (lib/schemas.ts) police the network boundary.
// A curated string as it arrives from the data. Three shapes, all resolved by
// label(): a bare string, a label object {en,de,fr,it}, and the KEYED form
// {key, name:{…}} that schema 1.1 uses for topic and source.
export type LabelObject = { [lang: string]: string | undefined };
export type Keyed = { key?: string; name?: LabelObject | string; [k: string]: unknown };
export type Translated = LabelObject | Keyed | string | null | undefined;
export interface Level { label?: Translated; data?: boolean }
export interface Dimension { label?: Translated; levels?: Record<string, Level>; hierarchy?: any }
export interface Meta {
  dim_order?: string[];
  dimensions?: Record<string, Dimension>;
  split?: string | null;
  single_select?: string | string[] | null;
  default?: Record<string, string>;
  title?: Translated;
  source?: { name?: Translated; url?: any } | string;
  [k: string]: any;
}
export type Selection = Record<string, string[]>;
/** A distinct data-bearing dim-tuple (from /dataset/<id>/combos). */
export type Combo = Record<string, string | undefined>;

import { getLocale } from "./locale";

/** i18n label: the current locale, falling back requested -> en -> any other
 * language; a bare string passes through. The one resolver every curated string
 * uses.
 *
 * Two shapes arrive from schema 1.1: a plain label object {en,de,fr,it} and a
 * KEYED one, {key: "prices", name: {en,de,…}} (topic, source). The wrapper is
 * unwrapped here rather than at each call site, so a field gaining a stable key
 * stays a data-side-only change.
 *
 * The fallback only ever considers LANGUAGE fields. It used to end at
 * Object.values(t)[0], which on a keyed object returned the key — so the
 * Details card rendered the topic as "prices" instead of "Preise"/"Prix", in
 * every language including English. A key is an identifier, never display text.
 */
const LANGS = ["en", "de", "fr", "it"] as const;
export const label = (t: Translated): string => {
  if (!t) return "";
  if (typeof t === "string") return t;
  if ((t as any).name !== undefined) return label((t as any).name);
  return (t[getLocale()] || t.en || LANGS.map(l => t[l]).find(Boolean) || "") as string;
};

/** Every language's text of a label object, space-joined — the SEARCH text.
 * Matching runs across all languages regardless of UI locale (a German user
 * typing "Bruttoinlandprodukt" and an English one pasting a French label both
 * hit); display then resolves through label(). */
export const allLangs = (t: Translated): string =>
  !t ? "" : typeof t === "string" ? t : Object.values(t).join(" ");

/** Source code from a dataset id: ch_snb_foo -> "SNB". */
export const badgeOf = (id: string): string => {
  const m = id.match(/^ch_([a-z]+)_/);
  return m ? (m[1] as string).toUpperCase() : "";
};

/** Display name of a dataset's source (object form or bare string). */
export const sourceName = (m: Meta): string =>
  label(typeof m.source === "object" ? m.source?.name : undefined) ||
  (typeof m.source === "string" ? m.source : "");

/** Any curated-string shape -> display string: bare string, {en,de,…} label
 * object, or a source object carrying {name: {…}}. The tolerant-reader rule:
 * every catalog/meta string field goes through this (or label()), so promoting
 * a field from string to label object is a data-side-only change. */
export const srcOf = (s: unknown): string =>
  typeof s === "string" ? s : label(((s as any)?.name ?? s) as Translated);

// The "split" dim is the one overlaid as multiple lines: the datasheet-declared
// `split`, else the dim with the most data-bearing levels. SNB cubes carry a
// hierarchy on nearly every dim, so hierarchy-presence is a poor selector —
// cardinality is better.
export function splitDimOf(meta: Meta): string | null {
  if (meta.split) return meta.split;
  let best: string | null = null, bestN = -1;
  for (const d of meta.dim_order || []) {
    const lv = meta.dimensions?.[d]?.levels || {};
    const n = Object.values(lv).filter(x => x.data !== false).length || Object.keys(lv).length;
    if (n > bestN) { bestN = n; best = d; }
  }
  return best;
}

// Dims the datasheet marks exclusive ("don't overlay these" — different units).
// Normalised to an array (the field may be a scalar, a list, or absent).
export function singleSelectDims(meta: Meta): string[] {
  const ss = meta.single_select;
  return ss == null ? [] : Array.isArray(ss) ? ss : [ss];
}

// Line-capable dims = dim_order minus single_select, IN dim_order. The split dim
// is always line-capable even if a datasheet mistakenly lists it single_select.
export function lineDims(meta: Meta): string[] {
  const ss = singleSelectDims(meta), split = splitDimOf(meta);
  return (meta.dim_order || []).filter(d => d === split || !ss.includes(d));
}

// The dataset's factory-default selection: the datasheet `default` code per dim
// (one line each), falling back to the first available level. `preselect` (from
// a sub-series search hit) overrides one dim.
export function defaultSelection(meta: Meta, preselect?: { dim?: string; code?: string }): Selection {
  const sel: Selection = {}, def = meta.default || {};
  for (const d of meta.dim_order || []) {
    const dflt = def[d];
    if (dflt != null) sel[d] = [dflt];
    else {
      const codes = Object.keys(meta.dimensions?.[d]?.levels || {});
      sel[d] = codes.length ? [codes[0] as string] : [];
    }
  }
  if (preselect && preselect.dim && preselect.code != null && meta.dimensions?.[preselect.dim])
    sel[preselect.dim] = [preselect.code];
  return sel;
}

/** The dims the stored default actually names, as a dims string ("D0=a;D1=b").
 * Unlike defaultSelection it invents nothing for the other dims, so it is the
 * query to retry with when the full default matches no rows. An empty default
 * arrives as [] for single-series datasets. */
export function pinnedDefault(meta: Meta): string {
  const def = meta.default && !Array.isArray(meta.default) ? meta.default : {};
  return (meta.dim_order || []).filter(d => def[d] != null).map(d => `${d}=${def[d]}`).join(";");
}

/** The selection of the first row in a /series frame that carries every dim,
 * or null when there is none. A missing code (ch_snb_bopcapbalq's "NA" code can
 * arrive as null) would re-query an empty combination, so such rows are skipped.
 * Codes are strings in the selection even when the engine sends numbers. */
export function firstRowSelection(frame: { date: unknown[]; [k: string]: any }, dims: string[]): Selection | null {
  const ds = dims.filter(d => Array.isArray(frame[d]));
  const n = frame.date ? frame.date.length : 0;
  if (!ds.length) return null;
  for (let r = 0; r < n; r++) {
    if (ds.some(d => frame[d][r] == null)) continue;
    const sel: Selection = {};
    for (const d of ds) sel[d] = [String(frame[d][r])];
    return sel;
  }
  return null;
}

/** True when a selection equals the factory default (order-insensitive per dim). */
export function selectionIsDefault(def: Selection, sel: Selection): boolean {
  const norm = (v?: string[]) => (v || []).slice().sort().join(" ");
  for (const d of new Set([...Object.keys(def), ...Object.keys(sel)]))
    if (norm(def[d]) !== norm(sel[d])) return false;
  return true;
}

// True iff some valid combo has tuple[dim] === level under the current
// single-select (exclusive) picks. Line dims don't constrain — the chart draws
// their cross-product and skips empty cells. Returns true when no combo index is
// available (graceful no-op). `constrainers` are the OTHER single-select dims.
export function levelPossible(
  combos: Combo[] | null | undefined,
  constrainers: string[],
  selection: Selection,
  dim: string,
  level: string,
): boolean {
  if (!combos || !combos.length) return true;
  const others = constrainers.filter(d => d !== dim);
  return combos.some(t => t[dim] === level &&
    others.every(d => { const sel = selection[d]; return !sel || !sel.length || sel.includes(t[d] as string); }));
}

// ---- naming what the chart shows --------------------------------------------

/** The hierarchy path to a level, root first, excluding the level itself;
 *  null when the code is not in the tree. */
function pathTo(hierarchy: any, code: string): string[] | null {
  if (!hierarchy || typeof hierarchy !== "object" || Array.isArray(hierarchy)) return null;
  for (const [c, kids] of Object.entries(hierarchy)) {
    if (c === code) return [];
    const p = pathTo(kids, code);
    if (p) return [c, ...p];
  }
  return null;
}

const enOf = (lv: Level | undefined): string => {
  const t = lv?.label;
  return (t && typeof t === "object" ? (t as LabelObject).en : typeof t === "string" ? t : "") || "";
};
const isTotalLabel = (en: string): boolean => /(^|[\s(:-])total\)?$/i.test(en.trim());

/** The parent of a level whose English label other levels share ("Total"
 *  under unemployed, under jobless rate, under vacancies), else null. */
function sharedParent(meta: Meta, dim: string, code: string): string | null {
  const d = meta.dimensions?.[dim];
  const mine = enOf(d?.levels?.[code]);
  const shared = Object.keys(d?.levels || {}).some(c => c !== code && enOf(d?.levels?.[c]) === mine);
  if (!shared) return null;
  const p = pathTo(d?.hierarchy, code);
  return p && p.length ? p[p.length - 1] as string : null;
}

/** A level that says nothing a title has to repeat: the dim's total (its only
 *  root, or a label ending in "total" that no other level shares), the only
 *  level the dim has, or a level with no label of its own. */
export function isHeadlineLevel(meta: Meta, dim: string, code: string): boolean {
  const d = meta.dimensions?.[dim];
  if (Object.keys(d?.levels || {}).length <= 1) return true;
  const en = enOf(d?.levels?.[code]);
  if (!en || en === code) return true;
  const h = d?.hierarchy;
  if (h && typeof h === "object" && !Array.isArray(h)) {
    const roots = Object.keys(h);
    if (roots.length === 1 && roots[0] === code) return true;
  }
  if (!isTotalLabel(en)) return false;
  const parent = sharedParent(meta, dim, code);   // a total under a named parent says which
  return !parent || isTotalLabel(enOf(d?.levels?.[parent]));
}

/** A level's name for a title. A label shared by several levels ("1 day" under
 *  SARON and under call money) gets its parent's in front; a shared total is
 *  named by its parent alone ("Jobless rate"). */
export function levelName(meta: Meta, dim: string, code: string): string {
  const d = meta.dimensions?.[dim];
  const own = label(d?.levels?.[code]?.label) || code;
  const parent = sharedParent(meta, dim, code);
  if (!parent) return own;
  const pl = label(d?.levels?.[parent]?.label) || parent;
  return isTotalLabel(enOf(d?.levels?.[code])) ? pl : `${pl}, ${own}`;
}

/** The chart's title: the dataset title, plus the level picked on each line dim
 *  that holds exactly one, unless it is the total. "Consumer prices (detailed
 *  basket): Actual rent paid", where the dataset title alone mislabelled every
 *  chart, image and file of a picked series. Same rule as ds_series_title in
 *  api/discover.R. */
export function seriesTitle(meta: Meta, selection: Selection): string {
  const base = label(meta.title);
  const parts: string[] = [];
  for (const d of lineDims(meta)) {
    const sel = selection[d] || [];
    if (sel.length !== 1 || isHeadlineLevel(meta, d, sel[0] as string)) continue;
    parts.push(levelName(meta, d, sel[0] as string));
  }
  return parts.length ? `${base}: ${parts.join(" · ")}` : base;
}

/** True for a units label object, false for the per-level shape. */
const isLangMap = (u: Record<string, unknown>): boolean =>
  Object.keys(u).every(k => (LANGS as readonly string[]).includes(k));

/** The unit label object of the selected series. `units` is dataset-wide
 *  ({en, de, …}) or keyed by the level codes of the unit-bearing dim: the first
 *  dim whose levels hold every key, so codes that recur in another dim ("1" as
 *  a region and as a measure) cannot pick the wrong unit. Same as
 *  ds_unit_label in api/discover.R. */
function unitObj(meta: Meta, selection: Selection): unknown {
  const u = meta.units;
  if (!u || typeof u !== "object" || Array.isArray(u)) return u;
  if (isLangMap(u)) return u;
  const keys = Object.keys(u);
  const dim = (meta.dim_order || []).find(d => keys.every(k => meta.dimensions?.[d]?.levels?.[k]));
  const code = dim ? (selection[dim] || [])[0] : undefined;
  return code != null ? (u as any)[code] : undefined;
}

/** The unit of the selected series, in the current language, or "" when the
 *  data declares none. */
export function unitOf(meta: Meta, selection: Selection): string {
  const o = unitObj(meta, selection);
  return typeof o === "string" ? o : o && typeof o === "object" ? label(o as LabelObject) : "";
}

/** The English unit of the selected series, for rules that read it. */
export function unitEnOf(meta: Meta, selection: Selection): string {
  const o = unitObj(meta, selection);
  return (typeof o === "string" ? o : o && typeof o === "object" ? (o as LabelObject).en : "") || "";
}

/** A unit that is a percentage (a rate, a share, a yield). A percent change of
 *  such a series is misleading (unemployment from 3.0 to 2.3 % reads as -23 %),
 *  so its changes are differences in percentage points. */
export const isPercentUnit = (unitEn: string): boolean =>
  /%|\bper ?cent\b|\bpercentage\b/i.test(unitEn);

/** How a selection's changes are computed: a factor for differences in
 *  percentage points (1 for a unit in percent, 100 for the 0-1 shares a
 *  datasheet lists in percent_levels), or undefined for percent changes. */
export function changeDiff(meta: Meta, selection: Selection): number | undefined {
  if (isPercentUnit(unitEnOf(meta, selection))) return 1;
  const pl: string[] | undefined = meta.percent_levels;
  const split = splitDimOf(meta);
  const sel = split ? selection[split] || [] : [];
  if (pl && pl.length && sel.length && sel.every(c => pl.includes(c))) return 100;
  return undefined;
}
